import axios, {
  AxiosInstance,
  AxiosStatic,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
} from 'axios';
import { auth } from './auth';

interface HttpContext {
  axios: AxiosStatic;
  baseURL?: string;
}

export type Http = AxiosInstance;

const baseURL = import.meta.env.VITE_APP_BASE_URL_API;

export function httpCtor({ axios }: HttpContext) {
  const http = axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
      'Access-Control-Allow-Origin': true,
    },
    timeout: 60000,
  });

  http.interceptors.request.use((config: AxiosRequestConfig) => {
    const token = auth.getToken();
    config.headers!.Authorization = `Bearer ${token}`;

    return config;
  });

  http.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
      console.error('http interceptor error', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        const { default: router, Pages } = await import('../router');

        if (error.response?.status === 401) {
          auth.signOut();
          const currentPage = router.currentRoute.value;
          router.push({ name: Pages.signIn, query: { next: currentPage.path } });
        }

        if (error.response?.status === 403) {
          router.replace({ name: Pages.notAllowed });
        }
      }

      return Promise.reject(error);
    },
  );

  return http;
}

export function getErrorMessage(error: unknown): string {
  if (error instanceof AxiosError) {
    if (error.response?.data?.message) {
      return error.response?.data?.message as string;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return `An unknown error occurred: ${error}`;
}

export function getErrorIssues(error: unknown) {
  if (error instanceof AxiosError) {
    return error.response?.data?.issues ?? [];
  }
  return [];
}
export function getErrorIssuesMessages(error: unknown): string[] {
  return getErrorIssues(error).map((issue: any) => {
    if (typeof issue === 'string') {
      return issue;
    }
    if (typeof issue === 'object' && issue.message) {
      return issue.message;
    }
    return `${issue}`;
  });
}

export default httpCtor({ axios });
